import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { X, CreditCard, Mail, Handshake, User as UserIcon, Plus, Minus, Package } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Customer } from '../../lib/customers';
import { startRentalAgreement, sendRentalPayLink, RentalStartMode, RentalStartResult } from '../../lib/rentals';
import { fetchCustomerPricing, CustomerPricing, EMPTY_PRICING } from '../../lib/customerPricing';
import SelectCustomerModal from '../manage/SelectCustomerModal';
import RecordPaymentModal from '../RecordPaymentModal';
import { SidePanel, Row } from './SidePanel';
import { Section, ProductPickerOverlay, PickerStockItem, rentalRateLabel } from './NewOrderModal';

interface RentalCustomer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  email: string | null;
  phone: string | null;
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const shortDate = (d: string) =>
  new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const MODES: { key: RentalStartMode; label: string; desc: string; icon: typeof CreditCard }[] = [
  {
    key: 'card_now',
    label: 'Collect card now',
    desc: 'Take a card payment for the first period right away (card saved on file). The rental only starts once the payment succeeds — same guarantee as online checkout.',
    icon: CreditCard,
  },
  {
    key: 'link',
    label: 'Send payment link',
    desc: 'Rental starts immediately. The first invoice is emailed with a pay link — when the customer pays, their card is saved for future cycles. Shows "No card on file" until then.',
    icon: Mail,
  },
  {
    key: 'silent',
    label: 'Start now, collect later',
    desc: 'Rental starts immediately and the first invoice is created, but nothing is sent. You follow up for payment and card details yourself.',
    icon: Handshake,
  },
];

/** Staff-side rental start: pick customer + rental item, set qty/rate, choose
 *  how payment is captured (card now / pay link / silently). */
export default function StartRentalModal({ businessId, presetCustomerId, onClose, onStarted, zBase = 100200 }: {
  businessId: string;
  presetCustomerId?: string;
  onClose: () => void;
  onStarted: () => void;
  zBase?: number;
}) {
  const [customer, setCustomer] = useState<RentalCustomer | null>(null);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [selected, setSelected] = useState<PickerStockItem | null>(null);
  const [qty, setQty] = useState(1);
  const [rateText, setRateText] = useState('');
  const [mode, setMode] = useState<RentalStartMode>('card_now');
  // First bill date (local): today = bill now; a future date defers billing
  // (first invoice generates on that date); a past date backdates it — the
  // first invoice is created now covering the period from that date, and
  // billing anchors to that day of month.
  const todayStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const [billDate, setBillDate] = useState(todayStr);
  const deferred = billDate > todayStr;
  const backdated = billDate < todayStr;
  const [pricing, setPricing] = useState<CustomerPricing>(EMPTY_PRICING);
  const [busy, setBusy] = useState(false);
  // card_now: after the RPC creates the pending agreement + invoice, the
  // payment modal opens on that invoice; the paid-invoice trigger activates it.
  const [payFor, setPayFor] = useState<RentalStartResult | null>(null);

  useEffect(() => {
    if (!presetCustomerId) return;
    supabase
      .from('customers')
      .select('id, first_name, last_name, company, email, phone')
      .eq('id', presetCustomerId)
      .single()
      .then(({ data }) => {
        if (data) setCustomer(data as RentalCustomer);
      });
  }, [presetCustomerId]);

  useEffect(() => {
    if (!customer?.id) { setPricing(EMPTY_PRICING); return; }
    fetchCustomerPricing(businessId, customer.id).then(setPricing).catch(() => setPricing(EMPTY_PRICING));
  }, [businessId, customer?.id]);

  const catalogRateCents = Number(selected?.rental_price_cents ?? 0);
  const effectiveDeposit = selected
    ? (pricing.deposits[selected.id] ?? Number(selected.deposit_cents) ?? 0)
    : 0;
  const interval = selected?.rental_interval === 'week' ? 'week' : 'month';
  const rateCents = Math.round((parseFloat(rateText) || 0) * 100);
  const firstInvoiceCents = rateCents * qty + effectiveDeposit * qty;
  const outOfStock = !!selected && selected.track_inventory && selected.quantity < qty;

  const handleSelectCustomer = useCallback((c: Customer) => {
    setCustomer({
      id: c.id,
      first_name: c.first_name ?? null,
      last_name: c.last_name ?? null,
      company: (c as { company?: string | null }).company ?? null,
      email: c.email ?? null,
      phone: c.phone ?? null,
    });
    setShowCustomerPicker(false);
  }, []);

  const handlePickItem = (item: PickerStockItem) => {
    setSelected(item);
    setQty(1);
    setRateText((Number(item.rental_price_cents ?? 0) / 100).toFixed(2));
    setShowItemPicker(false);
  };

  const customerName = customer
    ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() || customer.company || customer.email || 'Customer'
    : '';
  const customerInitials = customer
    ? `${(customer.first_name || ' ')[0]}${(customer.last_name || ' ')[0]}`.trim().toUpperCase() || 'C'
    : '';

  const start = async () => {
    if (!customer) { toast.error('Pick a customer first'); return; }
    if (!selected) { toast.error('Pick a rental item'); return; }
    if (rateCents <= 0) { toast.error('Rental rate must be greater than zero'); return; }
    if (!deferred && mode === 'link' && !customer.email) {
      toast.error('This customer has no email — use "Collect card now" or "Start now, collect later"');
      return;
    }
    setBusy(true);
    try {
      const res = await startRentalAgreement({
        businessId,
        customerId: customer.id,
        stockItemId: selected.id,
        quantity: qty,
        rateCents: rateCents === catalogRateCents ? null : rateCents,
        // Deferred billing has nothing to collect today — mode is moot.
        mode: deferred ? 'silent' : mode,
        firstBillDate: billDate !== todayStr ? billDate : null,
      });
      if (deferred) {
        toast.success(`Rental started — first invoice generates on ${shortDate(billDate)} and is collected automatically`, { duration: 6000 });
        onStarted();
        onClose();
        return;
      }
      if (mode === 'card_now') {
        setPayFor(res);
        return; // payment modal takes over; activation happens on payment
      }
      if (mode === 'link') {
        try {
          const sent = await sendRentalPayLink(businessId, res.invoice_id);
          toast.success(`Rental started — payment link sent to ${sent.to}`, { duration: 5000 });
        } catch (err) {
          toast.error(`Rental started, but the payment link email failed: ${err instanceof Error ? err.message : 'unknown error'}. Send it from the invoice.`, { duration: 8000 });
        }
      } else {
        toast.success(
          res.remaining_cents <= 0
            ? `Rental started — invoice #${res.invoice_number} covered by wallet`
            : `Rental started — invoice #${res.invoice_number} created (nothing sent)`,
          { duration: 5000 },
        );
      }
      onStarted();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start rental');
    } finally {
      setBusy(false);
    }
  };

  if (payFor) {
    return (
      <RecordPaymentModal
        isOpen
        onClose={() => {
          // Payment not completed — the rental stays pending. It's visible in
          // the Rentals list where staff can retry payment or cancel it.
          toast('Rental is pending — it starts once invoice #' + (payFor.invoice_number || '') + ' is paid. Collect payment or cancel it from the Rentals list.', { duration: 8000, icon: '⏳' });
          onStarted();
          onClose();
        }}
        onSuccess={() => {
          toast.success('Payment received — rental is live');
          onStarted();
          onClose();
        }}
        businessId={businessId}
        invoiceId={payFor.invoice_id}
        customerId={customer?.id}
        bookingTotal={payFor.total_cents / 100}
        amountPaid={0}
      />
    );
  }

  return (
    <SidePanel onClose={busy ? () => {} : onClose} width="md:w-[520px]" zIndex={zBase}>
      {/* Header — booking modal style */}
      <div className="relative bg-gray-50 border-b border-gray-200 px-6 py-5 text-center flex-shrink-0">
        <h3 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>Start Rental</h3>
        <button
          onClick={onClose}
          disabled={busy}
          className="absolute right-4 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-gray-200/70 hover:bg-gray-300 flex items-center justify-center"
        >
          <X className="w-[18px] h-[18px] text-gray-600" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 bg-gray-50/50">
        {/* Customer */}
        <Section
          title="Customer"
          action={customer && !presetCustomerId ? (
            <button onClick={() => setShowCustomerPicker(true)} className="text-[12px] font-semibold text-blue-600 hover:text-blue-700">
              Change
            </button>
          ) : undefined}
        >
          {customer ? (
            <div className="w-full bg-gray-50 rounded-xl p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[13px] font-bold flex-shrink-0">
                {customerInitials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-gray-900 truncate">{customerName}</p>
                {customer.company && (
                  <p className="text-[12px] font-medium text-gray-600 truncate">{customer.company}</p>
                )}
                <p className="text-[12px] text-gray-500 truncate">
                  {[customer.phone, customer.email].filter(Boolean).join(' · ') || 'No contact details'}
                </p>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowCustomerPicker(true)}
              className="w-full py-3 rounded-xl border-2 border-dashed border-blue-300 text-[13px] font-semibold text-blue-600 hover:bg-blue-50/50 transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <UserIcon className="w-4 h-4" /> Add customer
            </button>
          )}
        </Section>

        {/* Rental item */}
        <Section
          title="Rental item"
          action={selected ? (
            <button onClick={() => setShowItemPicker(true)} className="text-[12px] font-semibold text-blue-600 hover:text-blue-700">
              Change
            </button>
          ) : undefined}
        >
          {!selected ? (
            <button
              onClick={() => setShowItemPicker(true)}
              className="w-full py-6 rounded-xl border-2 border-dashed border-blue-300 text-[13px] font-semibold text-blue-600 hover:bg-blue-50/50 transition-colors"
            >
              <Plus className="w-4 h-4 inline mr-1 -mt-0.5" /> Add item
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50/40 px-3 py-2.5">
                <div className="w-12 h-12 bg-white rounded-lg border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {selected.photo_url ? (
                    <img src={selected.photo_url} alt={selected.name} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="h-5 w-5 text-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-gray-900 truncate">{selected.name}</p>
                  <p className="text-[12px] text-gray-500 truncate">
                    {selected.track_inventory === false ? 'Made to order' : `${selected.quantity} in stock`}
                    {effectiveDeposit > 0 && (
                      <span className="text-amber-600"> · +{money(effectiveDeposit)} deposit</span>
                    )}
                  </p>
                </div>
                <span className="text-[13px] font-semibold text-indigo-700 flex-shrink-0">{rentalRateLabel(selected)}</span>
              </div>

              {/* Qty + rate */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Quantity</label>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setQty(q => Math.max(1, q - 1))}
                      className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                    >
                      <Minus className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <span className="w-9 text-center text-[14px] font-semibold text-gray-900">{qty}</span>
                    <button
                      onClick={() => setQty(q => q + 1)}
                      className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                    >
                      <Plus className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Rate per unit / {interval}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">$</span>
                    <input
                      type="number" min={0} step="0.01" value={rateText}
                      onChange={(e) => setRateText(e.target.value)}
                      className="w-full pl-7 pr-3 py-2 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                    />
                  </div>
                  {rateCents !== catalogRateCents && rateCents > 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      Custom rate — catalog is {money(catalogRateCents)}. Locked in for this rental.
                    </p>
                  )}
                </div>
              </div>

              {/* First bill date — today bills now; a future date defers billing */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">First bill</label>
                <input
                  type="date"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value || todayStr)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                />
                {deferred && (
                  <p className="text-[11px] font-medium text-indigo-600 mt-1.5">
                    Equipment goes out and stock reserves now. The first invoice — first period{effectiveDeposit > 0 ? ' + deposit' : ''} —
                    generates on {shortDate(billDate)} and is collected automatically (wallet, saved card, or an emailed payment link).
                  </p>
                )}
                {backdated && (
                  <p className="text-[11px] font-medium text-amber-600 mt-1.5">
                    Backdated — the first invoice is created now and covers the period from {shortDate(billDate)},
                    with billing anchored to that day of the {interval}. If that's more than one {interval} ago, the
                    daily biller catches up the missed periods automatically.
                  </p>
                )}
              </div>

              {outOfStock && (
                <p className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  Only {selected.quantity} in stock — the rental can't start until stock covers it.
                </p>
              )}
            </div>
          )}
        </Section>

        {selected && (
          <>
            {/* First invoice summary */}
            <Section title={deferred ? `First invoice — ${shortDate(billDate)}` : 'First invoice'}>
              <div className="space-y-1">
                <Row label={`First period (${qty} × ${money(rateCents)})`} value={money(rateCents * qty)} />
                {effectiveDeposit > 0 && (
                  <Row
                    label={`Equipment deposit (${qty} × ${money(effectiveDeposit)})${pricing.deposits[selected.id] != null ? ' · custom' : ''}`}
                    value={money(effectiveDeposit * qty)}
                    accent="text-amber-700"
                  />
                )}
                <div className="border-t border-gray-200 pt-1.5 mt-1.5">
                  <Row label="First invoice (before tax)" value={money(firstInvoiceCents)} bold />
                </div>
                <p className="text-[11px] text-gray-400 pt-0.5">
                  {deferred
                    ? <>Nothing bills today. From {shortDate(billDate)}, {money(rateCents * qty)}/{interval} bills automatically. Deposit refunds when the equipment comes back.</>
                    : <>Then {money(rateCents * qty)}/{interval} billed automatically. Deposit refunds when the equipment comes back.</>}
                </p>
              </div>
            </Section>

            {/* Capture mode — moot when billing is deferred (nothing due today) */}
            {!deferred && (
              <Section title="How is payment collected?">
                <div className="space-y-1.5">
                  {MODES.map(m => (
                    <button
                      key={m.key}
                      onClick={() => setMode(m.key)}
                      className={`w-full flex gap-3 px-3.5 py-3 rounded-xl border-2 text-left transition-colors ${
                        mode === m.key ? 'border-blue-500 bg-blue-50/40' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <m.icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${mode === m.key ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span>
                        <span className="block text-[13px] font-semibold text-gray-900">{m.label}</span>
                        <span className="block text-[12px] text-gray-500 mt-0.5">{m.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
                {mode !== 'card_now' && customer && (
                  <p className="text-[11px] text-amber-600 mt-1.5">
                    The rental starts before payment — you're extending credit to {customerName}.
                  </p>
                )}
              </Section>
            )}
          </>
        )}
      </div>

      {/* Footer CTA — booking modal style */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3" style={{ boxShadow: '0 -2px 10px rgba(0,0,0,0.04)' }}>
        <button
          onClick={start}
          disabled={busy || !customer || !selected || rateCents <= 0 || outOfStock}
          className={`w-full py-3 rounded-xl text-[15px] font-semibold text-white transition-colors ${
            busy || !customer || !selected || rateCents <= 0 || outOfStock
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {busy ? 'Starting…'
            : deferred ? `Start rental — first bill ${shortDate(billDate)}`
              : mode === 'card_now' ? `Create & collect ${money(firstInvoiceCents)}`
                : mode === 'link' ? 'Start rental & email payment link'
                  : 'Start rental'}
        </button>
      </div>

      {showItemPicker && (
        <ProductPickerOverlay
          businessId={businessId}
          onPick={handlePickItem}
          onClose={() => setShowItemPicker(false)}
          rentalOnly
          zIndex={zBase + 30}
        />
      )}
      <SelectCustomerModal
        isOpen={showCustomerPicker}
        onClose={() => setShowCustomerPicker(false)}
        onSelect={handleSelectCustomer}
        baseZIndex={zBase + 50}
      />
    </SidePanel>
  );
}
